/**
 * React bindings for the live socket — docs/spec/03 §3/§5.
 *
 * One connection per session, opened once signed in and closed on logout, with
 * every consumer reading from context. Components never see the socket: they get
 * a status, a presence map, and enter/leave calls (HARD RULE 2 keeps the
 * transport inside `src/api/`).
 *
 * **Absent by default.** With no `VITE_WS_URL`, or nobody signed in, the provider
 * opens nothing and reports `stopped` — the app then runs entirely on HTTP with
 * polling (§5). That is also why the default context value below is a working
 * no-op: a screen rendered without this provider (as most tests do) must behave
 * exactly like a screen whose socket is switched off.
 */
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getConfig } from '../app/config'
import { matchesIdentifier } from '../lib/identity'
import { queryKeys } from '../lib/queryKeys'
import { currentUserIdentifiers, currentUserIsAdmin, useAuthStore } from './auth'
import {
  createLiveConnection,
  type LiveConnection,
  type LiveConnectionOptions,
  type LiveStatus,
  type LiveTopic,
  type PresenceUser,
} from './ws'
import { applyLiveEvent, type LiveCounts } from './wsCache'

/**
 * How long a `job.created` burst is collected before one refetch.
 *
 * A vendor uploading a batch emits one `job.created` per scan, and the api
 * debounces its own `counts` by 500 ms for the same reason. Refetching per event
 * would turn one upload into dozens of list requests — the exact behaviour 05 §5
 * calls out in the legacy client.
 */
const LIST_REFRESH_COALESCE_MS = 750

/** Polling cadence while the socket is not carrying updates (03 §5). */
export const FALLBACK_POLL_MS = 15_000

export interface LiveContextValue {
  status: LiveStatus
  /** Occupants by job id. Only jobs with occupants appear. */
  presence: Readonly<Record<string, readonly PresenceUser[]>>
  enterJob: (jobId: string) => void
  leaveJob: (jobId: string) => void
}

const OFFLINE: LiveContextValue = {
  status: 'stopped',
  presence: {},
  enterJob: () => {},
  leaveJob: () => {},
}

const LiveContext = createContext<LiveContextValue>(OFFLINE)

export interface LiveProviderProps {
  children?: ReactNode
  /** Injectable for tests; defaults to the real transport. */
  createConnection?: (options: LiveConnectionOptions) => LiveConnection
}

function topicsForCurrentUser(): LiveTopic[] {
  // `qa` is admin-only and the server enforces that (api §9). Asking for it as a
  // transcriber would earn a rejection, so role decides what we request.
  return currentUserIsAdmin() ? ['queue', 'qa'] : ['queue']
}

/**
 * Group a `presence` frame by job.
 *
 * Every frame names its job in a **top-level `job_id`** — including the
 * `users: []` one that clears the last occupant, which is the whole reason the
 * field was asked for (Q14, shipped 2026-07-29 and verified against staging the
 * same day). The occupant list replaces whatever we held for that job: a frame
 * is the *complete* roster, so merging would accumulate people who have left.
 *
 * A frame without `job_id` is **dropped, not guessed at.** There is no job to
 * apply it to, and inventing one from the per-user ids cannot attribute the empty
 * frame — the interim rule this replaced could only clear jobs *this* client had
 * entered, which is exactly the gap the api change closed. Returning `current`
 * unchanged also means React re-renders nothing.
 */
function groupPresence(
  event: { job_id?: string | undefined; users: readonly PresenceUser[] },
  current: Readonly<Record<string, readonly PresenceUser[]>>,
): Readonly<Record<string, readonly PresenceUser[]>> {
  if (event.job_id === undefined) return current

  const next = { ...current }
  if (event.users.length === 0) delete next[event.job_id]
  else next[event.job_id] = [...event.users]
  return next
}

export function LiveProvider({ children, createConnection }: LiveProviderProps) {
  const queryClient = useQueryClient()

  /**
   * Held in a ref, and deliberately NOT an effect dependency.
   *
   * What decides whether a socket should exist is the session and the config —
   * nothing about rendering. Depending on the factory's identity instead would
   * tear down and rebuild the connection on every parent re-render for any
   * caller passing an inline function, dropping live updates and re-running the
   * handshake for no reason.
   */
  const factoryRef = useRef(createConnection ?? createLiveConnection)
  factoryRef.current = createConnection ?? createLiveConnection

  const [status, setStatus] = useState<LiveStatus>('idle')
  const [presence, setPresence] = useState<Record<string, readonly PresenceUser[]>>({})

  /**
   * Reconnects on sign-in and sign-out only — NOT on every token refresh. The
   * handshake authenticates once; the server does not re-check mid-connection,
   * so tearing the socket down on each refreshed access token would drop live
   * updates every few minutes for no reason.
   */
  const signedIn = useAuthStore((state) => state.accessToken !== null)
  const wsUrl = getConfig().wsUrl

  const connectionRef = useRef<LiveConnection | null>(null)
  const enteredRef = useRef<Set<string>>(new Set())
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (wsUrl === null || !signedIn) {
      setStatus('stopped')
      return
    }

    const connection = factoryRef.current({
      url: wsUrl,
      topics: topicsForCurrentUser(),
      onStatus: setStatus,
      onEvent: (event) => {
        const { needsListRefresh } = applyLiveEvent(queryClient, event)

        if (event.type === 'presence') {
          setPresence((current) => groupPresence(event, current))
        }

        if (needsListRefresh && refreshTimerRef.current === null) {
          refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null
            // `refetchType: 'active'` — refresh what someone is looking at, not
            // every filter combination they happen to have cached.
            void queryClient.invalidateQueries({
              queryKey: queryKeys.jobs.lists(),
              refetchType: 'active',
            })
          }, LIST_REFRESH_COALESCE_MS)
        }
      },
    })
    connectionRef.current = connection

    // Presence the workspace declared before the socket existed (or across a
    // provider remount) still has to reach the server.
    for (const jobId of enteredRef.current) connection.enterJob(jobId)

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      connection.stop()
      connectionRef.current = null
      setPresence({})
    }
  }, [queryClient, signedIn, wsUrl])

  const enterJob = useCallback((jobId: string) => {
    enteredRef.current.add(jobId)
    connectionRef.current?.enterJob(jobId)
  }, [])

  const leaveJob = useCallback((jobId: string) => {
    enteredRef.current.delete(jobId)
    connectionRef.current?.leaveJob(jobId)
    setPresence((current) => {
      if (!(jobId in current)) return current
      const next = { ...current }
      delete next[jobId]
      return next
    })
  }, [])

  const value = useMemo<LiveContextValue>(
    () => ({ status, presence, enterJob, leaveJob }),
    [status, presence, enterJob, leaveJob],
  )

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>
}

/** Connection state, for the "live / reconnecting" indicator (04 §2). */
export function useLiveStatus(): LiveStatus {
  return useContext(LiveContext).status
}

/**
 * The polling interval the queue should use — `false` while the socket is
 * carrying updates (03 §5).
 *
 * Polling and a healthy socket together would double the work the socket exists
 * to remove, so exactly one of them runs at a time.
 */
export function useQueuePollInterval(): number | false {
  return useLiveStatus() === 'live' ? false : FALLBACK_POLL_MS
}

/**
 * Occupants of one job, plus presence declaration tied to the component's life.
 *
 * Entering on mount and leaving on unmount is what makes the indicator correct
 * when someone closes a tab mid-transcription: the server also releases a
 * dropped connection's jobs, so the two agree.
 */
export function useJobPresence(jobId: string | null): readonly PresenceUser[] {
  const { presence, enterJob, leaveJob } = useContext(LiveContext)
  const identifiers = currentUserIdentifiers()

  useEffect(() => {
    if (jobId === null || jobId === '') return
    enterJob(jobId)
    return () => {
      leaveJob(jobId)
    }
  }, [jobId, enterJob, leaveJob])

  /**
   * **Everyone except you.** The server echoes your own presence back (verified
   * on staging 2026-07-29), so without this the workspace would announce that you
   * are viewing the job you are looking at.
   *
   * Compared through `matchesIdentifier`, not `===`: presence returns the uuid
   * **unhyphenated** while other surfaces hyphenate it, so string equality would
   * fail to recognise you and show you to yourself.
   */
  return useMemo(() => {
    if (jobId === null) return []
    const occupants = presence[jobId] ?? []
    return occupants.filter((user) => !matchesIdentifier(user.id, identifiers))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, presence, identifiers.join('|')])
}

/**
 * Pushed queue counts, or `null` until a `counts` frame arrives.
 *
 * `null` is the honest answer rather than zeros: `subscribe` sends no snapshot
 * (03 §2), so an idle queue never emits one, and rendering `0 open` would be a
 * lie a caller cannot distinguish from a genuinely empty queue.
 */
export function useLiveCounts(): LiveCounts | null {
  /**
   * A real subscription, not `getQueryData`: reading the cache imperatively
   * returns the right value once and never re-renders when the next `counts`
   * frame lands, so the badge would freeze at whatever it showed first.
   *
   * `enabled: false` because nothing fetches this key — the socket is its only
   * writer — so the `queryFn` exists solely to satisfy the type and would be a
   * bug if it ever ran.
   */
  const { data } = useQuery<LiveCounts>({
    queryKey: queryKeys.live.counts(),
    // `skipToken` states "this query has no fetcher" in the type system, so
    // there is no unreachable `queryFn` body pretending it might run.
    queryFn: skipToken,
    staleTime: Infinity,
  })
  return data ?? null
}
